import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Info } from "lucide-react";
import { NumField, SelectField } from "../fields";
import ScoreIncrement from "../ScoreIncrement";
import SeverityLevelsEditor from "../SeverityLevelsEditor";
import { fillEmptyDimensionsWithPercentBands, normalizeInterpretationBasis, setDimensionScoreStep } from "../scoringCriteria";

/**
 * Universal Scoring Engine UI — 5 canonical methods:
 *   total_score, dominant_dimension, continuous_multi, combined_dimension, ranking
 *
 * Each method is presented as a card. Method-specific fields render below
 * (severity bands hint, combined dimension builder, per-dim ranges editor).
 */

const METHODS = [
  {
    value: "total_score",
    label_id: "Total Score",
    label_en: "Total Score",
    hint_id: "Semua item dijumlahkan menjadi satu skor total. Cocok untuk GAD-7, PHQ-9, Burnout, Stress. Tingkat keparahan diatur di bawah setelah metode ini dipilih.",
    hint_en: "All items summed into a single total score. Fits GAD-7, PHQ-9, Burnout, Stress. Severity levels are set below once this method is picked.",
  },
  {
    value: "dominant_dimension",
    label_id: "Profil Dominan",
    label_en: "Dominant Dimension",
    hint_id: "Skor per-dimensi, dimensi tertinggi menjadi profil dominan. Cocok untuk Bahasa Cinta, MBTI-style, gaya belajar.",
    hint_en: "Per-dimension scoring; the highest dimension wins as the dominant profile. Fits Love Language, MBTI-style, learning styles.",
  },
  {
    value: "continuous_multi",
    label_id: "Multi-Dimensi Kontinu",
    label_en: "Continuous Multi-Dimension",
    hint_id: "Setiap dimensi punya skor independen dan interpretasi sendiri. Cocok untuk Big Five, RIASEC, HEXACO. Setiap dimensi bisa punya range interpretasi sendiri di bawah.",
    hint_en: "Each dimension carries an independent score with its own interpretation. Fits Big Five, RIASEC, HEXACO. Each dimension can have its own range bands below.",
  },
  {
    value: "combined_dimension",
    label_id: "Dimensi Gabungan (Formula)",
    label_en: "Combined Dimension (Formula)",
    hint_id: "Skor baru dihitung dari formula gabungan dimensi lain (mis. Wellbeing = Positif + Flow − Stres). Bisa dibangun visual atau tulis ekspresi.",
    hint_en: "New scores derived by combining other dimensions via a formula (e.g. Wellbeing = Positive + Flow − Stress). Build visually or write an expression.",
  },
  {
    value: "ranking",
    label_id: "Peringkat (Ranking)",
    label_en: "Ranking",
    hint_id: "Semua dimensi diurutkan dari tertinggi ke terendah tanpa satu pemenang tunggal. Cocok untuk urutan preferensi/nilai.",
    hint_en: "All dimensions sorted highest to lowest without a single winner. Fits preference or value orderings.",
  },
];

export default function ScoringTab({ inst, set, criteriaByDim, setCriteriaBasis, addRange, updRange, rmRange, lang = "id" }) {
  const scoring = inst.scoring || {};
  const method = scoring.method || "continuous_multi";
  const setScoring = (patch) => set("scoring", { ...scoring, ...patch });

  const showPerDimBands = method === "continuous_multi" || method === "dominant_dimension" || method === "ranking";
  const showCombined = method === "combined_dimension";
  const showTotalHint = method === "total_score";
  // Older instruments may carry levels without total_score; keep them editable.
  const showSeverity = showTotalHint || (inst.severity_levels || []).length > 0 || inst.result_template_type === "category";

  return (
    <div className="space-y-6" data-wrap="responsive">
      {/* Method selector — 5 cards */}
      <div className="p-6 rounded-2xl border border-border bg-card space-y-4" data-testid="scoring-method-block">
        <div>
          <div className="font-display text-lg">{lang === "id" ? "Metode Skoring" : "Scoring Method"}</div>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            {lang === "id"
              ? "Pilih cara sistem menghitung dan menampilkan hasil. Sistem otomatis menyediakan output yang tepat untuk halaman hasil publik."
              : "Choose how the system computes and shows the outcome. Downstream layouts adapt automatically."}
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {METHODS.map((m) => {
            const active = method === m.value;
            return (
              <button
                key={m.value}
                type="button"
                onClick={() => setScoring({ method: m.value })}
                className={`text-left p-4 rounded-2xl border-2 transition ${active ? "border-terracotta-500 bg-terracotta-50" : "border-border bg-card hover:border-terracotta-300"}`}
                data-testid={`scoring-method-${m.value}`}
              >
                <div className={`font-display text-sm ${active ? "text-terracotta-700" : ""}`}>
                  {lang === "id" ? m.label_id : m.label_en}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1 leading-snug">{lang === "id" ? m.hint_id : m.hint_en}</div>
              </button>
            );
          })}
        </div>
        <div className="grid md:grid-cols-3 gap-4 pt-2">
          <NumField label={lang === "id" ? "Skala minimum" : "Scale min"} testid="s-min" value={scoring.scale_min} onChange={(v) => setScoring({ scale_min: v })} />
          <NumField label={lang === "id" ? "Skala maksimum" : "Scale max"} testid="s-max" value={scoring.scale_max} onChange={(v) => setScoring({ scale_max: v })} />
        </div>
      </div>

      {/* Total Score hint */}
      {showTotalHint && (
        <div className="p-4 rounded-2xl border border-teal-200 bg-teal-50/60 flex items-start gap-3" data-testid="scoring-total-hint">
          <Info className="w-4 h-4 mt-0.5 text-teal-700 shrink-0" />
          <div className="text-xs text-teal-800 leading-relaxed">
            {lang === "id"
              ? "Mode Total Score menjumlahkan seluruh item. Atur Tingkat (Minimal / Ringan / Sedang / Berat) di bawah. Sistem otomatis mencocokkan skor total ke tingkat yang tepat."
              : "Total Score sums all items. Set the levels (Minimal / Mild / Moderate / Severe) below. The engine matches totals to the right level automatically."}
          </div>
        </div>
      )}

      {showSeverity && (
        <SeverityLevelsEditor
          levels={inst.severity_levels || []}
          onChange={(arr) => set("severity_levels", arr)}
          lang={lang}
        />
      )}

      {/* Combined Dimensions builder */}
      {showCombined && (
        <CombinedDimensionsEditor
          combined={scoring.combined_dimensions || []}
          onChange={(arr) => setScoring({ combined_dimensions: arr })}
          dimensions={inst.dimensions || []}
          lang={lang}
        />
      )}

      {/* Per-dimension range editor — visible for all methods that use dimensions */}
      {showPerDimBands && (inst.dimensions || []).length > 0 && (
        <div className="space-y-6" data-testid="scoring-per-dim-block">
          <div className="px-1 flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="font-display text-lg">{lang === "id" ? "Interpretasi per Dimensi" : "Per-Dimension Interpretation"}</div>
              <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                {lang === "id"
                  ? "Untuk setiap dimensi, tentukan rentang skor + label + interpretasi. Ditampilkan pada kartu dimensi di halaman hasil."
                  : "For each dimension, define score ranges + label + interpretation. Shown on the result page dimension card."}
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="rounded-full"
              onClick={() => {
                const { scoring: next, filled } = fillEmptyDimensionsWithPercentBands(scoring, inst.dimensions || []);
                if (filled) set("scoring", next);
              }}
              title={lang === "id" ? "Rendah 0–40%, Sedang 40–60%, Tinggi 60–100% untuk dimensi yang belum punya rentang" : "Low 0–40%, Moderate 40–60%, High 60–100% for dimensions without ranges"}
              data-testid="s-fill-percent-bands"
            >
              {lang === "id" ? "Isi Rendah/Sedang/Tinggi (%)" : "Fill Low/Moderate/High (%)"}
            </Button>
          </div>
          {inst.dimensions.map((d) => {
            const criterion = criteriaByDim.get(d.id);
            const ranges = criterion?.ranges || [];
            const interpretationBasis = normalizeInterpretationBasis(criterion?.interpretation_basis);
            return (
              <div key={d.id} className="p-5 rounded-2xl border border-border bg-card">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div>
                    <div className="font-display text-lg">{d.name_id || d.code}</div>
                    <div className="text-xs text-muted-foreground">{lang === "id" ? "Rentang skor untuk dimensi ini" : "Score ranges for this dimension"}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => addRange(d.id)} data-testid={`s-add-range-${d.code}`}>
                    <Plus className="w-3 h-3 mr-1" /> {lang === "id" ? "Rentang" : "Range"}
                  </Button>
                </div>
                <label className="block mb-4 max-w-sm">
                  <span className="block text-xs font-medium mb-1">{lang === "id" ? "Dasar interpretasi rentang" : "Range interpretation basis"}</span>
                  <select value={interpretationBasis} onChange={(event) => setCriteriaBasis(d.id, event.target.value)} className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid={`s-interpretation-basis-${d.code}`}>
                    <option value="raw_score">{lang === "id" ? "Skor mentah (jumlah)" : "Raw score (sum)"}</option>
                    <option value="average">{lang === "id" ? "Rata-rata per item" : "Average per item"}</option>
                    <option value="percentage">{lang === "id" ? "Persentase (0–100)" : "Percentage (0–100)"}</option>
                  </select>
                  <span className="block text-[11px] text-muted-foreground mt-1">{lang === "id" ? "Nilai ini dibandingkan dengan min–max setiap rentang. Persentase = posisi skor antara skor terendah dan tertinggi yang mungkin, jadi tidak tergantung jumlah item." : "This value is compared with each range's min–max. Percentage = where the score sits between the lowest and highest possible score, so it does not depend on the item count."}</span>
                </label>
                <ScoreIncrement value={criterion?.score_step} code={d.code} lang={lang}
                  onChange={(value) => set("scoring", setDimensionScoreStep(scoring, d.id, value))} />
                <div className="space-y-2">
                  {ranges.map((r, ri) => (
                    <div key={ri} className="grid grid-cols-12 gap-2 items-center" data-testid={`s-range-${d.code}-${ri}`}>
                      <Input placeholder="code" value={r.code || ""} onChange={(e) => updRange(d.id, ri, { code: e.target.value.trim().toLowerCase() })} className="col-span-1 h-9 font-mono text-xs" data-testid={`s-range-code-${d.code}-${ri}`} />
                      <Input type="number" placeholder="min" value={r.min} onChange={(e) => updRange(d.id, ri, { min: parseFloat(e.target.value) || 0 })} className="col-span-1 h-9" />
                      <Input type="number" placeholder="max" value={r.max} onChange={(e) => updRange(d.id, ri, { max: parseFloat(e.target.value) || 0 })} className="col-span-1 h-9" />
                      <Input placeholder="Label ID" value={r.label_id} onChange={(e) => updRange(d.id, ri, { label_id: e.target.value })} className="col-span-2 h-9" />
                      <Input placeholder="Label EN" value={r.label_en} onChange={(e) => updRange(d.id, ri, { label_en: e.target.value })} className="col-span-2 h-9" />
                      <Input placeholder="Interp ID" value={r.interpretation_id} onChange={(e) => updRange(d.id, ri, { interpretation_id: e.target.value })} className="col-span-2 h-9" />
                      <Input placeholder="Interp EN" value={r.interpretation_en} onChange={(e) => updRange(d.id, ri, { interpretation_en: e.target.value })} className="col-span-2 h-9" />
                      <Button size="icon" variant="ghost" onClick={() => rmRange(d.id, ri)} className="col-span-1 text-destructive">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function CombinedDimensionsEditor({ combined, onChange, dimensions, lang }) {
  const upd = (i, patch) => onChange(combined.map((c, k) => k === i ? { ...c, ...patch } : c));
  const rm = (i) => onChange(combined.filter((_, k) => k !== i));
  const add = () => onChange([
    ...combined,
    {
      id: cryptoRandomId(),
      code: `c${combined.length + 1}`,
      name_id: "",
      name_en: "",
      description_id: "",
      description_en: "",
      formula: "",
      operands: [],
    },
  ]);

  return (
    <div className="p-6 rounded-2xl border border-border bg-card space-y-4" data-testid="scoring-combined-block">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="font-display text-lg">{lang === "id" ? "Dimensi Gabungan" : "Combined Dimensions"}</div>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            {lang === "id"
              ? "Buat skor baru dari kombinasi dimensi lain. Contoh: Wellbeing = Positif + Flow − Stres. Gunakan mode visual untuk klik-tambah, atau mode formula untuk ekspresi mentah."
              : "Create new scores by combining other dimensions. Example: Wellbeing = Positive + Flow − Stress. Use the visual mode or write raw expressions."}
          </p>
        </div>
        <Button onClick={add} variant="outline" className="rounded-full" data-testid="scoring-combined-add">
          <Plus className="w-4 h-4 mr-1" /> {lang === "id" ? "Tambah" : "Add"}
        </Button>
      </div>

      {combined.length === 0 && (
        <div className="text-xs text-muted-foreground italic">
          {lang === "id" ? "Belum ada dimensi gabungan. Klik “Tambah” untuk membuat." : "No combined dimensions yet. Click “Add” to create one."}
        </div>
      )}

      <div className="space-y-4">
        {combined.map((cd, i) => (
          <CombinedRow
            key={cd.id || i}
            index={i}
            cd={cd}
            dimensions={dimensions}
            upd={(patch) => upd(i, patch)}
            rm={() => rm(i)}
            lang={lang}
          />
        ))}
      </div>
    </div>
  );
}


function CombinedRow({ index, cd, dimensions, upd, rm, lang }) {
  const [advanced, setAdvanced] = useState(!!(cd.formula && (!cd.operands || cd.operands.length === 0)));

  const operands = cd.operands || [];
  const setOperands = (arr) => {
    upd({ operands: arr, formula: operandsToFormula(arr) });
  };

  const addDim = (code) => setOperands([...operands, { kind: "dim", code, aggregate: "sum" }]);
  const addOp = (op) => setOperands([...operands, { op }]);
  const addConst = () => setOperands([...operands, { kind: "const", value: 1 }]);
  const removeAt = (i) => setOperands(operands.filter((_, k) => k !== i));

  const dimCodes = (dimensions || []).map((d) => ({ value: d.code, label: `${d.code} — ${d.name_id || d.name_en || ""}` }));

  return (
    <div className="p-4 rounded-xl border border-border bg-secondary/30 space-y-3" data-testid={`scoring-combined-row-${index}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground w-14">Code</Label>
        <Input value={cd.code} onChange={(e) => upd({ code: e.target.value })} className="h-8 max-w-[8rem] font-mono text-xs" data-testid={`scoring-combined-code-${index}`} />
        <label className="ml-auto flex items-center gap-2 text-xs">
          <Switch checked={advanced} onCheckedChange={setAdvanced} data-testid={`scoring-combined-advanced-${index}`} />
          {lang === "id" ? "Mode Formula" : "Formula Mode"}
        </label>
        <Button size="icon" variant="ghost" onClick={rm} className="text-destructive" data-testid={`scoring-combined-rm-${index}`}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Nama (ID)" : "Name (ID)"}</Label>
          <Input value={cd.name_id || ""} onChange={(e) => upd({ name_id: e.target.value })} placeholder={lang === "id" ? "cth: Kesejahteraan" : "e.g. Wellbeing"} data-testid={`scoring-combined-name-id-${index}`} />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Nama (EN)" : "Name (EN)"}</Label>
          <Input value={cd.name_en || ""} onChange={(e) => upd({ name_en: e.target.value })} placeholder="e.g. Wellbeing" data-testid={`scoring-combined-name-en-${index}`} />
        </div>
      </div>

      {!advanced ? (
        <div className="space-y-2">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{lang === "id" ? "Bangun Formula" : "Build Formula"}</div>
          <div className="flex flex-wrap gap-2 items-center p-3 rounded-xl bg-background border border-border min-h-[44px]" data-testid={`scoring-combined-visual-${index}`}>
            {operands.length === 0 && (
              <span className="text-xs text-muted-foreground italic">{lang === "id" ? "Tambah dimensi / operator dari bawah…" : "Add a dimension / operator below…"}</span>
            )}
            {operands.map((op, i) => (
              <button
                key={i}
                type="button"
                onClick={() => removeAt(i)}
                title={lang === "id" ? "Hapus" : "Remove"}
                className={`px-2 py-0.5 rounded-full text-xs font-mono border ${op.op ? "bg-teal-100 text-teal-800 border-teal-200" : op.kind === "const" ? "bg-cream-100 text-teal-800 border-border" : "bg-terracotta-100 text-terracotta-700 border-terracotta-200"}`}
              >
                {op.op || (op.kind === "dim" ? `dim_${op.code}_${op.aggregate || "sum"}` : op.kind === "const" ? String(op.value) : op.kind)}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <select
              className="h-8 rounded-full px-3 border border-border bg-background text-xs"
              value=""
              onChange={(e) => { if (e.target.value) { addDim(e.target.value); e.target.value = ""; } }}
              data-testid={`scoring-combined-dim-select-${index}`}
            >
              <option value="">+ {lang === "id" ? "dimensi" : "dimension"}</option>
              {dimCodes.map((d) => (<option key={d.value} value={d.value}>{d.label}</option>))}
            </select>
            {["+", "-", "*", "/", "(", ")"].map((op) => (
              <Button key={op} size="sm" variant="outline" className="h-7 px-3 rounded-full text-xs" onClick={() => addOp(op)}>{op}</Button>
            ))}
            <Button size="sm" variant="outline" className="h-7 px-3 rounded-full text-xs" onClick={addConst}>+ konst</Button>
            <Button size="sm" variant="ghost" className="h-7 px-3 rounded-full text-xs text-destructive" onClick={() => setOperands([])}>{lang === "id" ? "Bersihkan" : "Clear"}</Button>
          </div>
          <div className="text-[11px] font-mono text-muted-foreground pt-1">
            {lang === "id" ? "Preview: " : "Preview: "}<span className="text-teal-800">{operandsToFormula(operands) || "—"}</span>
          </div>
        </div>
      ) : (
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Formula (ekspresi)" : "Formula (expression)"}</Label>
          <Textarea
            rows={2}
            value={cd.formula || ""}
            onChange={(e) => upd({ formula: e.target.value, operands: [] })}
            placeholder="dim_pos_sum + dim_flow_sum - dim_stress_sum"
            className="font-mono text-xs"
            data-testid={`scoring-combined-formula-${index}`}
          />
          <div className="text-[11px] text-muted-foreground mt-1">
            {lang === "id"
              ? "Variabel: dim_<code>_sum, dim_<code>_avg, dim_<code>_count, total, n. Operator: + − × ÷ ( )."
              : "Variables: dim_<code>_sum, dim_<code>_avg, dim_<code>_count, total, n. Operators: + − × ÷ ( )."}
          </div>
        </div>
      )}
    </div>
  );
}


function operandsToFormula(operands) {
  const parts = [];
  for (const op of operands || []) {
    if (!op) continue;
    if (op.op) parts.push(op.op);
    else if (op.paren) parts.push(op.paren);
    else if (op.kind === "dim") parts.push(`dim_${op.code}_${op.aggregate || "sum"}`);
    else if (op.kind === "total") parts.push("total");
    else if (op.kind === "n") parts.push("n");
    else if (op.kind === "const") parts.push(String(op.value ?? 0));
  }
  return parts.join(" ").trim();
}


function cryptoRandomId() {
  try {
    return (crypto?.randomUUID && crypto.randomUUID()) || Math.random().toString(36).slice(2);
  } catch { return Math.random().toString(36).slice(2); }
}
